@if ((isLoading$ | async) || (parentIsLoading$ | async)) {
  <div class="spinner">
    <mat-progress-spinner color="primary" mode="indeterminate"> </mat-progress-spinner>
  </div>
}

<!-- There is a corner case that the violation is undefined and but the loading stage was over.
    Until this behavior will be re-written, we should check that the violation is not undefined -->
@if (violation$ | async; as violation) {
  @if ((isLoading$ | async) === false && (parentIsLoading$ | async) === false && !showNoData && violation) {
    <div class="violation">
      <div class="violation-header">
        <div class="violation-header-title">
          @if (iconPath) {
            <img class="icon" [src]="iconPath" />
          }

          <div class="violation-header-title-text">
            <span class="violation-name-text" appLongTextTooltip [matTooltip]="violation.rule.displayName">
              {{ violation.rule.displayName }}
            </span>
            @if (checkIfErrorExistsAndPopulateErrorMessage() | async) {
              <div class="violation-error-icon">
                <img
                  class="status-label-icon"
                  src="assets/images/status-icons/error.svg"
                  alt="Error"
                  [matTooltip]="this.errorMessage$ | async"
                  style="padding-top: 3px"
                />
              </div>
            }
            <div class="violation-tags-container">
              <app-multiple-tags [tags]="(ruleTags$ | async) || ''"></app-multiple-tags>
            </div>
          </div>
        </div>

        <div class="violation-header-secondary-title">
          <div class="violation-header-secondary-title-property">
            <div>Violation</div>
          </div>
        </div>

        <div class="violation-header-secondary-title first-seen-container">
          <div class="violation-header-secondary-title-property">
            <div>
              {{
                ('violationPage.firstSeenTitle' | labelsProvider) +
                  ' ' +
                  (violation.firstSeen
                    | date
                      : 'MMM
                    d,y,HH:mm')
              }}
            </div>
          </div>
          <div class="separator">|</div>
          <div class="violation-header-secondary-title-property">
            <div>{{ 'violationPage.idTitle' | labelsProvider }}</div>
            <app-copy-button
              class="copy-button"
              [value]="violation.id"
              [title]="violation.id"
              [isSecondaryFontColor]="true"
            >
            </app-copy-button>
          </div>
        </div>

        <div class="violation-header-secondary-title">
          <div class="violation-header-secondary-title-property last-seen">
            <div>
              {{
                ('violationPage.lastSeenTitle' | labelsProvider) +
                  ' ' +
                  (violation.lastSeen
                    | date
                      : 'MMM
                    d,y,HH:mm')
              }}
            </div>
            <div class="info-icon">
              <img
                src="assets/images/common/info.svg"
                width="14"
                [matTooltip]="'violationPage.lastSeenTooltip' | labelsProvider"
                matTooltipPosition="right"
                matTooltipClass="tooltip__white tooltip__multiline"
              />
            </div>
            @if (violation.refreshMethod === RefreshMethodEnum.eventDriven) {
              <div class="lightning-icon">
                <img
                  src="assets/images/common/lightning.svg"
                  width="20"
                  [matTooltip]="'violationPage.lightningTooltip' | labelsProvider"
                  matTooltipPosition="right"
                  matTooltipClass="tooltip__white tooltip__multiline"
                />
              </div>
            }
          </div>
        </div>

        <div class="violation-header-tertiary-title">
          <div class="violation-header-tertiary-title-property">
            <app-severity-label [severity]="violation.rule.severity" [smallSize]="true"></app-severity-label>
            <div class="separator">|</div>
            <app-status-label [statusLabel]="violationStatusLabel" [statusDescription]="violationStatusDescription">
            </app-status-label>
          </div>
        </div>

        <div class="violation-header-quaternary-title">
          <div class="violation-header-quaternary-title-property violation-header-quaternary-title-property-clickable">
            <app-hyperlink-button
              buttonIconPath="assets/images/common/label.svg"
              buttonTextPath="violationPage.labels"
              (buttonClickedEventEmitter)="addLabelsClicked()"
            ></app-hyperlink-button>
            <div class="separator">|</div>
            <app-view-labels-multi-select
              [labelBindings]="violation.labelBindings"
              (removeLabelEventEmitter)="removeLabelBindingFromViolation($event)"
            ></app-view-labels-multi-select>
            @if (isUpdatingLabels$ | async) {
              <mat-progress-spinner class="small-spinner" color="primary" mode="indeterminate"></mat-progress-spinner>
            }
          </div>
        </div>

        <app-sidepanel-tabs
          class="violation-header-tabs"
          [tabs]="tabs"
          (tabOnSelection)="onTabSelect($event)"
          [currentTabIndex]="(this.activeTabIndex$ | async) ?? 0"
        >
        </app-sidepanel-tabs>
      </div>

      <div class="violation-actions">
        @if (isActionRunningSpinner) {
          <mat-progress-spinner class="small-spinner" color="primary" mode="indeterminate" style="left: -20px">
          </mat-progress-spinner>
        }

        <div class="actions">
          <app-single-page-actions
            class="actions"
            [menuActions]="(menuActions$ | async) || []"
            (actionSelected)="actionSelected($event)"
          >
          </app-single-page-actions>
        </div>
      </div>

      <div class="violation-body">
        @if ((activeTabIndex$ | async) === 0 && violation) {
          <div class="violation-body-tab">
            <div class="violation-body-tab-card policy-card">
              <app-accordion
                class="violation-body-tab-card-title"
                [accordionTitle]="'violationPage.graphCardTitle'"
                [boldTitle]="true"
                [withLabelPipe]="true"
                [expanded]="accordionExpansionStateMapping['violationPage.graphCardTitle']"
                (accordionExpansionEvent)="updateAccordionExpansionState('violationPage.graphCardTitle', $event)"
              >
                @if (isGraphExist() | async) {
                  <app-relations-graph [integrationId]="integration()" [resourceId]="violation.resource.id" />
                } @else {
                  <div>
                    {{ this.graphNotExistLabel }}
                  </div>
                }
              </app-accordion>
            </div>

            <div class="violation-body-tab-card policy-card">
              <app-accordion
                class="violation-body-tab-card-title"
                [accordionTitle]="'violationPage.summaryCardTitle'"
                [boldTitle]="true"
                [withLabelPipe]="true"
                [expanded]="accordionExpansionStateMapping['violationPage.summaryCardTitle']"
                (accordionExpansionEvent)="updateAccordionExpansionState('violationPage.summaryCardTitle', $event)"
              >
                <div>
                  {{ violation.rule.summary === '' ? violation.rule.description : violation.rule.summary }}
                </div>

                <app-card-fields
                  [cardFields]="(summaryCardFields$ | async) || []"
                  [isToggleContentAllowed]="true"
                ></app-card-fields>

                @if (findingsCardFieldsList$ | async; as findingsCardFieldsList) {
                  @if (findingsCardFieldsList.length === 1 && Object.keys(findingsCardFieldsList[0]).length > 0) {
                    <div class="violation-body-tab-card-inner">
                      <div class="violation-body-tab-card-inner-title">
                        {{ 'violationPage.singlefindingCardTitle' | labelsProvider }}
                      </div>
                      <app-card-fields [cardFields]="findingsCardFieldsList[0]" [isToggleContentAllowed]="true">
                      </app-card-fields>
                    </div>
                  }

                  @if (findingsCardFieldsList.length > 1) {
                    <app-carousel-card
                      class="violation-body-tab-card-inner"
                      [cardFieldsList]="findingsCardFieldsList"
                      titleLabelPath="violationPage.findingsCardTitle"
                      [isToggleContentAllowed]="true"
                    >
                    </app-carousel-card>
                  }
                }

                <a
                  class="explore-resource-link"
                  (click)="exploreResourceInventory(violation)"
                  [class.disabled]="(isLinkExist() | async) === false"
                  [matTooltip]="(isLinkExist() | async) ? '' : linkNotExistTooltip"
                >
                  {{ 'violationPage.summaryCardExploreResourceInventoryLink' | labelsProvider }}
                </a>
              </app-accordion>
            </div>

            @if (violation.rule.riskReason !== '') {
              <div class="violation-body-tab-card policy-card">
                <app-accordion
                  class="violation-body-tab-card-title"
                  [accordionTitle]="'violationPage.riskReasonCardTitle'"
                  [boldTitle]="true"
                  [withLabelPipe]="true"
                  [expanded]="accordionExpansionStateMapping['violationPage.riskReasonCardTitle']"
                  (accordionExpansionEvent)="updateAccordionExpansionState('violationPage.riskReasonCardTitle', $event)"
                >
                  <div>{{ violation.rule.riskReason }}</div>
                  <app-security-tag [tags]="(owaspRiskTag$ | async) || []"></app-security-tag>
                  <app-security-tag [tags]="(owaspNhiRiskTag$ | async) || []"></app-security-tag>
                  <app-security-tag [tags]="(attackRiskTag$ | async) || []"></app-security-tag>
                  <app-security-tag [tags]="(securityRiskTag$ | async) || []"></app-security-tag>
                </app-accordion>
              </div>
            }

            <div class="violation-body-tab-card policy-card">
              <app-accordion
                class="violation-body-tab-card-title"
                [accordionTitle]="'violationPage.triageSuggestionTitle'"
                [boldTitle]="true"
                [withLabelPipe]="true"
                [expanded]="accordionExpansionStateMapping['violationPage.triageSuggestionTitle']"
                (accordionExpansionEvent)="updateAccordionExpansionState('violationPage.triageSuggestionTitle', $event)"
              >
                <app-timeline [timelineItems]="(triageSuggestion$ | async) || []" [withIcons]="false"></app-timeline>
              </app-accordion>
            </div>

            @if (violation.rule.fixSuggestion !== '') {
              <div class="violation-body-tab-card policy-card">
                <app-accordion
                  class="violation-body-tab-card-title"
                  [accordionTitle]="'violationPage.fixSuggestionTitle'"
                  [boldTitle]="true"
                  [withLabelPipe]="true"
                  [expanded]="accordionExpansionStateMapping['violationPage.fixSuggestionTitle']"
                  (accordionExpansionEvent)="updateAccordionExpansionState('violationPage.fixSuggestionTitle', $event)"
                >
                  @if (fixSuggestion$ | async; as fixSuggestion) {
                    @if (fixSuggestion.length > 0) {
                      <app-timeline [timelineItems]="fixSuggestion" [withIcons]="false" [violationMode]="true">
                      </app-timeline>
                    }

                    @if (fixSuggestion.length === 0) {
                      <div>
                        {{ violation.rule.fixSuggestion }}
                      </div>
                    }

                    <div class="fix-suggestion-copy-container">
                      <app-copy-button
                        [value]="getFixSuggestionCopyText(violation)"
                        [text]="'Copy'"
                        [withCopyIcon]="true"
                        [isSecondaryFontColor]="false"
                      />
                    </div>
                  }
                </app-accordion>
              </div>
            }
          </div>
        }

        @if ((activeTabIndex$ | async) === 1) {
          <div class="violation-body-tab note-card">
            <div class="violation-body-tab note-card-header-container">
              <div class="note-tab-header">
                {{ notesNumberTitle$ | async }}
              </div>
              <div class="note-tab-header-separator"></div>

              @if (isAddOrDeleteNoteAllowed()) {
                <div class="add-note-container">
                  <div class="add-note-text-box-container">
                    <textarea
                      class="add-note-text-box"
                      [ngClass]="{
                        'add-note-text-box-invalid':
                          noteContentFormControl.invalid && noteContentFormControl.value.length > 0,
                      }"
                      [formControl]="noteContentFormControl"
                      maxlength="{{ noteMaxLength }}"
                      placeholder="{{ noteTextAreaPlaceholder }}"
                    ></textarea>
                  </div>
                  <div class="add-note-button-container">
                    @if (notesLoading$ | async) {
                      <div class="spinner notes-spinner">
                        <mat-progress-spinner color="primary" mode="indeterminate"> </mat-progress-spinner>
                      </div>
                    }

                    <button
                      class="add-note-button"
                      (click)="addNote()"
                      [disabled]="!noteContentFormControl.valid"
                      [ngClass]="{
                        'add-note-button-disabled': !noteContentFormControl.valid,
                      }"
                    >
                      {{ 'violationPage.addNoteButton' | labelsProvider }}
                    </button>
                  </div>
                </div>
              }
              @if (isAddOrDeleteNoteAllowed()) {
                <div class="note-tab-header-separator"></div>
              }
            </div>
            <div class="violation-body-tab note-card-bottom-container">
              @for (note of violationNotes$ | async; track note.id) {
                <div class="violation-body-tab-card">
                  <div class="violation-body-tab-card-title note-card-title">
                    <div class="note-card-added-by">
                      {{ note.addedBy }}
                    </div>
                    <div class="note-card-added-at">
                      {{ note.addedAt }}
                    </div>

                    @if (isAddOrDeleteNoteAllowed()) {
                      <div class="note-card-delete-container">
                        <img
                          class="violation-header-title-close-button"
                          src="assets/images/common/delete.svg"
                          (click)="noteDeleteOnClick(note.id)"
                        />
                      </div>
                    }
                  </div>
                  <div class="note-card-content">
                    {{ note.content.toString() }}
                  </div>
                </div>
              }
            </div>
          </div>
        }

        @if ((activeTabIndex$ | async) === 2) {
          <div class="violation-body-tab audit-card">
            <div class="violation-body-tab audit-card-header-container">
              <div class="audit-tab-header">
                {{ auditsNumberTitle$ | async }}
              </div>
              <div class="audit-tab-header-separator"></div>
            </div>
            <div class="resource-body-tab-card audit-card">
              <app-timeline
                [timelineItems]="(violationAudits$ | async) || []"
                [withIcons]="false"
                [timelineItemsDescOrder]="true"
              ></app-timeline>
            </div>
          </div>
        }
      </div>
    </div>
  }
}

@if (showNoData && !((isLoading$ | async) || (parentIsLoading$ | async))) {
  <div class="no-data-container">
    <app-no-data [inDialog]="true"></app-no-data>
  </div>
}

<!-- ZenAI Assistant -->
@if (featureFlagsService.isFeatureEnabled(featureFlagTypes.ZenAI)) {
  <app-zen-ai-assistant [domain]="zenAIDomain.Violations"></app-zen-ai-assistant>
}
